import './prototypes/array.js';

import { 
    Line, 
    lerp 
} from './prototypes/math/graphs.js';
import {
    BezierCurve
} from './prototypes/math/bezier.js';

import { CanvasObject } from "./objects/object.js";
import { VectorObject } from "./objects/vector.js";
import { FrameObject } from "./objects/frame.js";
import { ImageObject } from './objects/image.js';
import { GroupObject } from './objects/group.js';
import { storeKey } from 'vuex';

class Creator {
    constructor({
        canvasElement,
        exportElement = null,
        saveState = null
    }) {
        this.context = canvasElement.getContext('2d');
        this.exportElement = exportElement;
        this.canvasOffset = {
            x: this.context.canvas.x,
            y: this.context.canvas.y
        }
        this.position = {
            x: saveState ? saveState.position.x : 0,
            y: saveState ? saveState.position.y : 0
        }
        // this.dimensions = {
        //     width: 0,
        //     height: 0
        // }
        
        this.zoom = 1;

        this.objects = [];
        if (saveState && Array.isArray(saveState.objects) && saveState.objects.length > 0) {
            this.objects = saveState.objects
                .filter(obj => obj && obj.type)
                .map(obj => {
                    if (obj.type === "VectorObject") {
                        return new VectorObject({
                            context: this.context,
                            saveState: obj
                        });
                    } else if (obj.type === "FrameObject") {
                        return new FrameObject({
                            context: this.context,
                            saveState: obj
                        });
                    } else if (obj.type === "ImageObject") {
                        return new ImageObject({
                            context: this.context,
                            saveState: obj
                        });
                    }

                    return null;
                })
                .filter(Boolean);
        }

        this.tools = {
            select: {
                selected: saveState ? saveState.tools.select?.selected : false
            },
            pen: {
                selected: saveState ? saveState.tools.pen?.selected : false
            },
            hand: {
                selected: saveState ? saveState.tools.hand?.selected : false
            },
            text: {
                selected: saveState ? saveState.tools.text?.selected : false
            },
            frame: {
                selected: saveState ? saveState.tools.frame?.selected : false
            },
            image: {
                selected: saveState ? saveState.tools.image?.selected : false
            }
        };

        this.controls = {
            mouse: {
                x: 0,
                y: 0
            }
        };
        this._events = {
            mousedown: false,
            mouseup: false,
            dragging: false,
            editing: false,
            multiselect: false
        };
        this.animationFrame = null;

        this.init();
    }
    _dpr() { return window.devicePixelRatio || 1; }
    _updateCanvas() {
        const canvas = this.context.canvas;
        const canvasRect = canvas.getBoundingClientRect();
        const stage = canvas.parentElement;
        const stageRect = stage.getBoundingClientRect();

        // console.log('canvasRect', canvasRect);

        // console.log('stage', stage);
        // console.log('stageRect', stageRect);
        // console.log('this.dimensions',this.dimensions);

        this.canvasOffset = {
            x: stageRect.x * this._dpr(),
            y: stageRect.y * this._dpr()
        }
        this.dimensions = {
            width: canvasRect.width,
            height: canvasRect.height
        }


        this.context.canvas.style['width'] = `${this.dimensions.width}px`;
        this.context.canvas.style['height'] = `${this.dimensions.height}px`;

        this.context.canvas.width = this.dimensions.width * this._dpr();
        this.context.canvas.height = this.dimensions.height * this._dpr();
    }
    _updateCursor({x, y}) {
        this.controls.mouse.x = x;
        this.controls.mouse.y = y;
    }
    _handToolOnMouseMove() {
        this.position = {
            x: this.controls.mouse.x - this.position.before.x,
            y: this.controls.mouse.y - this.position.before.y,
            before: this.position.before
        };

        this.context.canvas.dispatchEvent(
            new CustomEvent(
                'creator:position',
                {
                    detail: {
                        x: this.position.x * -1, 
                        y: this.position.y * -1
                    }
                }
            )
        );
    }
    _penToolOnMouseDown() {
        this.editing = true;
        const vertex = {
            x: (this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1)) / this.zoom,
            y: (this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)) / this.zoom
        }
        let selected_object = this._getObjects({
            selected: true,
            editing: true,
            type: VectorObject,
            ensure: true
        });
        
        selected_object.editing = true;
        selected_object.addVertex(vertex);

        // console.log('you are drawing a vector point with the pen');
    }
    _canvasEvents() {
        const canvas = this.context.canvas;

        // Smooth pan support for trackpads / mouse wheels
        const handleWheelPan = (event) => {
            // Prevent default scroll/navigation so the gesture pans the canvas only
            event.preventDefault();

            const lineHeight = 16;
            const pageHeight = this.dimensions.height || 800;
            const multiplier = event.deltaMode === 1
                ? lineHeight
                : event.deltaMode === 2
                    ? pageHeight
                    : 1;

            const deltaX = event.deltaX * multiplier;
            const deltaY = event.deltaY * multiplier;

            this.position = {
                x: this.position.x - deltaX,
                y: this.position.y - deltaY
            };

            this.context.canvas.dispatchEvent(new CustomEvent('creator:position', {
                detail: {
                    x: this.position.x * -1,
                    y: this.position.y * -1
                }
            }));

            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = requestAnimationFrame(this.refresh.bind(this));
        };
        canvas.addEventListener('wheel', handleWheelPan, { passive: false });

        canvas.addEventListener('creator:inspector:update', (event) => {
            const object = event.detail;

            this.objects.forEach((o_context) => {
                if (o_context.uid !== object.uid) return;

                if (object.position) {
                    o_context.position = {
                        ...o_context.position,
                        ...object.position
                    };
                }

                if (object.dimensions) {
                    o_context.dimensions = {
                        ...o_context.dimensions,
                        ...object.dimensions
                    };
                }

                if (object.fill) {
                    o_context.fill = {
                        ...o_context.fill,
                        ...object.fill
                    };
                }

                if (object.stroke) {
                    o_context.stroke = {
                        ...o_context.stroke,
                        ...object.stroke
                    };
                }

                if (object.effects) {
                    o_context.effects = {
                        ...o_context.effects,
                        ...object.effects
                    };
                }
            });

            this.refresh();
        });
        canvas.oncontextmenu = (event) => {
            event.preventDefault();
            // console.log('this depends if its on a object or not');

            // it depends where the cursor is, that will determine which context menu to show
            let [selected_object] = this.objects.filter( o_context => {
                return o_context.isCursorOnObject({
                    x: (this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1)) / this.zoom,
                    y: (this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)) / this.zoom
                }) || (o_context.selected && o_context._cursorInBounds({
                    x: (this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1)) / this.zoom,
                    y: (this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)) / this.zoom
                }));
            });

            if( selected_object ){
                canvas.dispatchEvent(new CustomEvent('creator:object:contextmenu', {
                    detail: {
                        open: true,
                        object: selected_object,
                        position: {
                            x: this.controls.mouse.x / this._dpr(),
                            y: this.controls.mouse.y / this._dpr()
                        }
                    }
                }));
            }
        }
        window.onkeydown = (event) => {
            if( event.key.toLocaleLowerCase() === 'escape' ) {
                this._events.editing = false;
                this.context.canvas.dispatchEvent(new CustomEvent('creator:object:edit:finish', {
                    detail: {
                        object: this
                    }
                }));
                canvas.dispatchEvent(new CustomEvent('creator:object:contextmenu', {
                    detail: {
                        open: false,
                        object: null,
                        position: {
                            x: 0,
                            y: 0
                        }
                    }
                }));
            }
        };
        window.onmousemove = (event) => {
            this._updateCursor({
                x: event.x * this._dpr(),
                y: event.y * this._dpr()
            });

            if( this._events.mousedown ) this._events.dragging = true;

            const selected_tool = this._getSelectedTool();
            if( this._events.dragging ) {
                if( selected_tool === 'hand' ) {
                    this._handToolOnMouseMove();
                } else if( selected_tool === 'select' ) {
                    this._selectToolOnMouseMove();
                }
            } else {
                this._selectToolOnMouseMove({
                    detect_hover: true
                });
            }
            if( selected_tool === 'pen' ) {
                this.context.canvas.dispatchEvent(new CustomEvent('creator:pen:move', {detail: this}));
            }
            this.animationFrame = requestAnimationFrame( this.refresh.bind(this) );
        };
        window.ondblclick = (event) => {
            this._updateCursor({
                x: event.x * this._dpr(),
                y: event.y * this._dpr()
            });

            const selected_tool = this._getSelectedTool();
            
            if( selected_tool === 'select' ) {
                this._selectToolOnDblClick();
            }

            this.animationFrame = requestAnimationFrame(this.refresh.bind(this));
        };
        canvas.onmousedown = (event) => {
            this._updateCursor({    
                x: event.x * this._dpr(),
                y: event.y * this._dpr()
            });

            const selected_tool = this._getSelectedTool();

            this._events.mousedown = true;
            this.position.before = {
                x: this.controls.mouse.x - this.position.x,
                y: this.controls.mouse.y - this.position.y
            }
            this.controls.mouse.before = {
                x: this.controls.mouse.x,
                y: this.controls.mouse.y
            }

            if( selected_tool === 'pen' ) {
                this._penToolOnMouseDown();
            } else if( selected_tool === 'text' ) {
                console.log('creating a text object on canvas');
            } else if( selected_tool === 'select' ) {
                this._selectToolOnMouseDown();
            } else if( selected_tool === 'frame' ) {
                this._frameToolOnMouseDown();
            } else if( selected_tool === 'image' ) {
                this._imageToolOnMouseDown();
            }

            // temp way of making it disappear.
            canvas.dispatchEvent(new CustomEvent('creator:object:contextmenu', {
                detail: {
                    open: false,
                    object: null,
                    position: {
                        x: 0,
                        y: 0
                    }
                }
            }));

            this.animationFrame = requestAnimationFrame( this.refresh.bind(this) );
        };
        window.onmouseup = (event) => { 
            if( this._events.multiselect ) {
                const zone = this.multiselectZone;

                const zoneLeft   = Math.min(zone.x, zone.x + zone.width);
                const zoneRight  = Math.max(zone.x, zone.x + zone.width);
                const zoneTop    = Math.min(zone.y, zone.y + zone.height);
                const zoneBottom = Math.max(zone.y, zone.y + zone.height);

                // select only objects whose boundingRect intersects the multiselectZone
                const selectedObjects = this.objects.filter(o_context => {
                    const rect = o_context.boundingRect;
                    if (!rect) return false;

                    const objLeft   = rect.x;
                    const objRight  = rect.x + rect.width;
                    const objTop    = rect.y;
                    const objBottom = rect.y + rect.height;

                    const intersects =
                        objLeft <= zoneRight &&
                        objRight >= zoneLeft &&
                        objTop <= zoneBottom &&
                        objBottom >= zoneTop;

                    return intersects;
                });

                this.objects.forEach(o_context => {
                    o_context.selected = selectedObjects.includes(o_context);
                    // keep existing editing state logic; only deselect objects
                    if (!o_context.selected) {
                        o_context.editing = false;
                    }
                });

                if (selectedObjects.length) {
                    const [first] = selectedObjects;
                    this.context.canvas.dispatchEvent(
                        new CustomEvent('creator:object:selected', {
                            detail: { object: first }
                        })
                    );
                }

                this._events.multiselect = false;
                this.multiselectZone = null;
                this.refresh();
                return;
            }

            Object.keys(this._events).forEach(event => {
                this._events[event] = false;
            });

            this._events.mouseup = true;

            const selected_object = this._getObjects({selected: true});

            if(
                selected_object && 
                selected_object instanceof VectorObject 
            ) {
                selected_object.stopVertexDrag();
                selected_object.stopControlPointDrag();
            }

            this.objects.forEach( o_context => {
                // o_context.selected = false;
                o_context.dragging = false;
                o_context.resizing = false;
                if (
                    o_context.vertexDrag && 
                    selected_object instanceof VectorObject
                ) {
                    o_context.vertexDrag = null;
                }
            });

            // this.context.canvas.dispatchEvent(new CustomEvent('creator:object:deselect_all', {
            //     detail: {
            //         object: this.selected
            //     }
            // }));

            this.refresh();

            cancelAnimationFrame(this.animationFrame);
        };
        window.onresize = (event) => this.refresh();
    }
    _appendTools({tools = []} = {}) {
        this.tools = {
            pen: {
                selected: false
            },
            hand: {
                selected: true
            },
            text: {
                selected: false
            },
            ...tools
        };
    }
    _getSelectedTool() {
        return Object.keys(this.tools).find(tool => this.tools[tool].selected);
    }
    _selectToolOnMouseMove({
        detect_hover=false
    } = {}) {
        const updateObjectEvent = () => {
            this.context.canvas.dispatchEvent(new CustomEvent('creator:object:update', 
                { 
                    detail: {
                        context: this.context,
                        object: this.selected_object
                    }
                }
            ));
        }

        // show outline when object hovered over
        // console.log(this.objects);
        if( detect_hover ) {
            const objects = this.objects.filter( o_context => {
                o_context.hovered = false;

                return o_context.isCursorOnObject({
                    x: (this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1)) / this.zoom,
                    y: (this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)) / this.zoom
                });
            });

            if( objects.length ) objects.first().hovered = true;

            return;
        }

        // we need get all selected object incase of a multiselect
        this.selected_object = (this.selected_object === null ) ? this._getObjects({selected: true}) : this.selected_object;

        if( !this.selected_object ) {
            if( this._events.multiselect ) {
                this.multiselectZone = {
                    x: (this.controls.mouse.before.x - this.canvasOffset.x + (this.position.x * -1)) / this.zoom,
                    y: (this.controls.mouse.before.y - this.canvasOffset.y + (this.position.y * -1)) / this.zoom,
                    width: (this.controls.mouse.x - this.controls.mouse.before.x) / this.zoom,
                    height: (this.controls.mouse.y - this.controls.mouse.before.y) / this.zoom
                };
            }
            return;
        };

        if( this.selected_object.dragging ) {
            this.selected_object.moveTo({
                x: (this.controls.mouse.x - this.controls.mouse.before.x) / this.zoom,
                y: (this.controls.mouse.y - this.controls.mouse.before.y) / this.zoom,
                before: this.controls.mouse.before
            });
            updateObjectEvent();
        } else if( this.selected_object.resizing ) {
            this.selected_object.resize({
                position: {
                    x: this.selected_object.position.x + ((this.controls.mouse.x - this.controls.mouse.before.x)/this.zoom),
                    y: this.selected_object.position.y + ((this.controls.mouse.y - this.controls.mouse.before.y)/this.zoom),
                    before: this.selected_object.position.before
                },
                dimensions: {
                    width: this.selected_object.dimensions.before.width + ((this.controls.mouse.x - this.controls.mouse.before.x)/this.zoom),
                    height: this.selected_object.dimensions.before.height + ((this.controls.mouse.y - this.controls.mouse.before.y)/this.zoom),
                    before: this.selected_object.dimensions.before,
                },
                cursor: {
                    x: (this.controls.mouse.x - this.controls.mouse.before.x) / this.zoom,
                    y: (this.controls.mouse.y - this.controls.mouse.before.y) / this.zoom
                }
            });
            updateObjectEvent();
        } else if( this.selected_object.editing ) {
            // find the vertex that has a flag dragging=True
            // this.selected_object.findVertex
            
            // the logic below is meant for VectorObject
            if( !(this.selected_object instanceof VectorObject) ) return;
            const vertex_index = this.selected_object._getVertexIndex({ dragging: true });

            this.selected_object.dragVertex({
                x: this.controls.mouse.x - this.controls.mouse.before.x,
                y: this.controls.mouse.y - this.controls.mouse.before.y,
                vertex_index
            });
            this.selected_object.dragControlPoint({
                x: this.controls.mouse.x - this.controls.mouse.before.x,
                y: this.controls.mouse.y - this.controls.mouse.before.y
            });

            updateObjectEvent();
        }
    }
    _selectToolOnMouseDown() {
        // first check for selected objects
        const selected_objects = this.objects.filter( o_context => o_context.selected );

        this.selected_object = null;

        if( selected_objects.length > 1 ) {
            // we need to create a custom temp selected_object
            // remember this object cannot be edited
            this.selected_object = new GroupObject({context: this.context});
            this.selected_object.addObjectToGroup({o_context: selected_objects});
        } else {
            // these are objects that is under the cursor
            let objects = this.objects.filter( o_context => {
                return o_context.isCursorOnObject({
                    x: (this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1)) / this.zoom,
                    y: (this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)) / this.zoom
                });
            });
    
            // and this selects the object that is under the cursor when the left mouse is down.
            // we need to consider multi select zones in order to drag more than one selected objects
            if( objects.length ) this.selectObject({object: objects[0], editing: objects[0].editing});

            this.selected_object = this._getObjects({selected: true});
        }

        if( !this.selected_object ) {
            this._events.multiselect = true;
            this.controls.mouse.before = {
                x: this.controls.mouse.x, 
                y: this.controls.mouse.y
            };

            // we need determine if its a multiselectzone
            this.multiselectZone = {
                x: (this.controls.mouse.before.x - this.canvasOffset.x + (this.position.x * -1)) / this.zoom,
                y: (this.controls.mouse.before.y - this.canvasOffset.y + (this.position.y * -1)) / this.zoom,
                width: (this.controls.mouse.x - this.controls.mouse.before.x) / this.zoom,
                height: (this.controls.mouse.y - this.controls.mouse.before.y) / this.zoom
            };
            
            return;
        };

        if( this.selected_object.editing ) {
            this._objectEditing();
        } else {
            const drag_bounds = this.selected_object._cursorInBounds({
                x: (this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1)) / this.zoom,
                y: (this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)) / this.zoom
            });
            const resize_boundary = this.selected_object._cursorInResizeBounds({
                x: (this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1)) / this.zoom,
                y: (this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)) / this.zoom
            });

            if( drag_bounds ) {
                this.selected_object.startDragObject({ controls: this.controls });
            } else if( resize_boundary ) {
                this.selected_object.startResizeObject({ controls: this.controls, resize_boundary });
            } else {
                this.objects.forEach( o_context => {
                    o_context.selected = false;
                });
            }
        }
    }
    _selectToolOnDblClick() {
        const selected_object = this._getObjects();

        if( !selected_object ) return;

        this.selectObject({object: selected_object, editing: true});
        this._objectEditing({setCurve: true});
    }
    _frameToolOnMouseDown() {
        // create a frame
        // switch to "select" tool
        const _object_pos = {
            x: this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1),
            y: this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)
        }

        const _object = this.createObject({
            type: FrameObject
        });

        if(_object instanceof FrameObject) if(_object.setPosition) _object.setPosition(_object_pos);
        if(_object instanceof FrameObject) if(_object.setDimensions) _object.setDimensions({w: 200, h: 200});
    }
    _imageToolOnMouseDown() {
        const _object_pos = {
            x: this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1),
            y: this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)
        }

        const _object = this.createObject({type: ImageObject});

        if(_object instanceof ImageObject) if(_object.setPosition) _object.setPosition(_object_pos);
        if(_object instanceof ImageObject) if(_object.setDimensions) _object.setDimensions({w: 200, h: 200});
    }
    _objectEditing({
        setCurve = false
    } = {}) {
        const selected_object = this._getObjects({selected: true, editing: true});

        if( !selected_object ) return;

        // temp edit mode fix
        // because this is only meant for VectorObject
        if( !(selected_object instanceof VectorObject) ) return;

        // find if user is trying to move a vertex
        const vertex_index = selected_object.isCursorOnVertex({
            x: this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1),
            y: this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)
        });
        const control_point = selected_object.isCursorOnControlPoint({
            x: this.controls.mouse.x - this.canvasOffset.x + (this.position.x * -1),
            y: this.controls.mouse.y - this.canvasOffset.y + (this.position.y * -1)
        });

        if( Number.isFinite(vertex_index) && setCurve ) {
            selected_object.vertices[vertex_index].curve = setCurve;
            selected_object._createControlPoints({vertex_index});
        } else if( control_point ) { // control points
            selected_object.startDragControlPoint({
                x: this.controls.mouse.x - this.canvasOffset.x + this.position.x * -1,
                y: this.controls.mouse.y - this.canvasOffset.y + this.position.y * -1,
                control_point
            });
        } else if( Number.isFinite(vertex_index) ) {
            selected_object.startDragVertex({
                x: this.controls.mouse.x - this.canvasOffset.x + this.position.x * -1,
                y: this.controls.mouse.y - this.canvasOffset.y + this.position.y * -1,
                vertex_index
            });
        }
    }
    _getObjects({
        selected = true,
        editing = undefined,
        type = null,
        ensure = false
    } = {}) {
        const selected_object = this.objects.find((context) => {
            if (selected !== undefined && context.selected !== selected) return false;
            if (type && !(context instanceof type)) return false;
            if (editing !== undefined && context.editing !== editing) return false;
            return true;
        });

        if (ensure && !selected_object) return this.createObject({ type });

        return selected_object;
    }
    init() {
        this._updateCanvas();
        this._canvasEvents();
        
        this.refresh();
    }
    getState() {
        return {
            position: { ...this.position },
            dimensions: { ...this.dimensions },
            tools: { ...this.tools },
            objects: [ ...this.objects.map(obj => obj.getState()) ],
            zoom: this.zoom
        }
    }
    renderMultiSelect() {
        const ctx = this.context;

        if( !this.multiselectZone ) {
            this.multiselectZone = {x: 0, y: 0, width: 0, height: 0};
        }

        ctx.strokeStyle = 'rgba(0, 0, 255, 0.5)';
        ctx.fillStyle = 'rgba(0, 0, 255, 0.1)';

        ctx.beginPath();
        ctx.rect(
            this.multiselectZone.x,
            this.multiselectZone.y, 
            this.multiselectZone.width, 
            this.multiselectZone.height
        );
        ctx.stroke();
        ctx.fill();
        ctx.closePath();
    }
    refresh() {
        this._updateCanvas();
        const ctx = this.context;

        ctx.translate(this.position.x, this.position.y);
        ctx.scale(this.zoom, this.zoom);

        this.objects.forEach(obj => obj.render());

        if( this._events.multiselect ) this.renderMultiSelect();
    }
    selectTool(tool) {
        const all_tools = Object.keys(this.tools);

        all_tools.forEach( t => {
            if( t.toLowerCase() === tool.toLowerCase() ) {
                this.tools[t].selected = true;
            } else {
                this.tools[t].selected = false;
            }
        });

        const selected_tool = this._getSelectedTool();

        this.context.canvas.dispatchEvent(new CustomEvent('creator:tool:selected', {detail: {selected_tool}}));

        return selected_tool;
    }
    selectObject({
        object, 
        editing = false
    }) {
        this.objects.forEach( o_context => {
            if( o_context.uid === object.uid ){
                this.context.canvas.dispatchEvent(new CustomEvent('creator:object:selected', {detail: {object}}));
                // we not allowing a frame object to be edited
                o_context.selected = true;
                o_context.editing = (o_context instanceof FrameObject) ? false : editing;
            } else {
                o_context.selected = false;
            }
        });
    }
    createObject({
        type
    } = {}) {
        // by default we select object after its creation
        // perhaps presets for basic objects like, rectangle, triangle, circle, star

        let _object = null;
        
        // ensure all objects are deselected
        this.objects.forEach( context => {
            context.selected = false;
            context.editing = false;
        } );

        if( type === VectorObject ) {
            _object = new VectorObject({
                context: this.context
            });

            _object.selected = true;
            _object.editing = true; // this is only if its a new vector
        } else if( type === FrameObject ) {
            _object = new FrameObject({
                context: this.context
            });

            _object.selected = true;

            this.selectTool('select');
        } else if( type === ImageObject ) {
            _object = new ImageObject({
                context: this.context
            });

            _object.selected = true;

            this.selectTool('select');
        }

        if(_object) this.objects.push(_object);
        
        return _object;
    }
    deleteObject({
        uid
    }) {
        this.objects = this.objects.filter(obj => obj.uid !== uid);

        this.context.canvas.dispatchEvent(new CustomEvent('creator:object:delete', {detail: {uid}}));
    }
    exportObject({
        uid
    }) {
        const export_context = this.exportElement.getContext('2d');
        const [_object] = this.objects.filter(obj => obj.uid === uid);

        this.exportElement.style['width'] = `${_object.dimensions.width}px`;
        this.exportElement.style['height'] = `${_object.dimensions.height}px`;

        export_context.canvas.width = _object.dimensions.width * this._dpr();
        export_context.canvas.height = _object.dimensions.height * this._dpr();

        export_context.strokeStyle = 'white';
        export_context.fillStyle = 'white';
        export_context.lineWidth = 2;

        // this adds the background to the vector;
        if( false ) { // tranparency
            export_context.beginPath();
            export_context.rect(
                0,
                0,
                _object.dimensions.width,
                _object.dimensions.height
            );
            export_context.fill();
            export_context.closePath();
        }

        _object.vertices.forEach( (v, i) => {
            if( v.control_points ) {
                v.control_points.forEach( cp => {
                    cp.x = cp.x - _object.position.x;
                    cp.y = cp.y - _object.position.y;
                });
            }
            v.x = v.x - _object.position.x;
            v.y = v.y - _object.position.y;
        });

        _object.drawPath({context: export_context});

        export_context.canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'export.png';
            link.click();
            URL.revokeObjectURL(url);
        }, 'image/png');

        _object.vertices.forEach( (v, i) => {
            if( v.control_points ) {
                v.control_points.forEach( cp => {
                    cp.x = cp.x + _object.position.x;
                    cp.y = cp.y + _object.position.y;
                });
            }
            v.x = v.x + _object.position.x;
            v.y = v.y + _object.position.y;
        });

    }
}

export {
    Creator
}

